import { useEffect, useState } from "react";
import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import LoadingDialog from "@/components/ui/LoadingDialog";
import VideoPlayer from "@/components/Media/VideoPlayer";
import { useVideoEntity } from "@/api/api";
import { toastError } from "@/utils/toast";
import { toCssWidth } from "@/utils/utils";

export const VIDEO_PLAY_DIALOG_DEFAULT_WIDTH = "100%";

export interface VideoPlayDialogProps {
  readonly open: boolean;
  readonly projectId: string | null;
  readonly videoId: string | null;
  readonly onClose: () => void;
  readonly width?: string | number;
}

export default function VideoPlayDialog({ open, projectId, videoId, onClose, width: widthProp }: VideoPlayDialogProps) {
  const [playerReady, setPlayerReady] = useState(false);
  const { data: videoEntityData, isPending: videoEntityLoading, isError } = useVideoEntity(open ? projectId : null, open ? videoId : null);

  const playbackUrl = videoEntityData?.url;
  const showVideo = open && !videoEntityLoading && typeof playbackUrl === "string" && playbackUrl.length > 0;

  const frameWidth = toCssWidth(widthProp ?? VIDEO_PLAY_DIALOG_DEFAULT_WIDTH);

  useEffect(() => {
    if (!open) {
      setPlayerReady(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || videoEntityLoading) {
      return;
    }

    if (isError) {
      toastError("Could not load video");
      onClose();
    }
  }, [open, videoEntityLoading, isError, onClose]);

  return (
    <>
      <LoadingDialog open={open && videoEntityLoading} label="Loading video…" />
      <Dialog
        open={showVideo}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: (theme) => ({
              backgroundColor: alpha(theme.palette.common.black, 0.4)
            })
          },
          paper: {
            sx: {
              borderRadius: 2,
              maxHeight: "min(90dvh, 95vh)"
            }
          },
          transition: {
            onEntered: () => {
              setPlayerReady(true);
            },
            onExited: () => {
              setPlayerReady(false);
            }
          }
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            p: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
            boxSizing: "border-box"
          }}
        >
          <Box
            sx={{
              width: frameWidth,
              maxWidth: "100%",
              aspectRatio: "16 / 9",
              position: "relative",
              overflow: "hidden",
              borderRadius: 1,
              bgcolor: "common.black",
              alignSelf: "center"
            }}
          >
            {showVideo && playerReady && videoId ? (
              <VideoPlayer
                key={`${videoId}:${playbackUrl}`}
                src={playbackUrl}
                sourceMimeType="video/mp4"
                autoPlay
                enableCrossOrigin={false}
                containerSx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  flex: 1,
                  minHeight: 0
                }}
                onPlaybackError={() => {
                  toastError("Could not play video");
                }}
              />
            ) : null}
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={(theme) => ({
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "action.focus",
              color: theme.palette.common.white,
              transition: theme.transitions.create(["background-color", "transform"], {
                duration: theme.transitions.duration.shorter
              }),
              "&:hover": {
                bgcolor: "action.hover"
              },
              "&:active": {
                bgcolor: alpha(theme.palette.common.black, 0.85)
              }
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </>
  );
}
